import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  AlertTriangle,
  Cat,
  User,
  Users,
  MapPin,
  Phone,
  Camera,
  Upload,
  X,
  Loader2,
  Clock,
  Eye,
  Share2,
  CheckCircle2,
  Search,
  Navigation,
  Heart
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Alert type configuration
const ALERT_TYPES = {
  animal: {
    label: "Animal disparu",
    icon: Cat,
    color: "#F59E0B",
    bgClass: "bg-amber-500",
    textClass: "text-amber-600"
  },
  elderly: {
    label: "Personne agee",
    icon: User,
    color: "#EF4444",
    bgClass: "bg-red-500",
    textClass: "text-red-600"
  },
  young: {
    label: "Jeune disparu",
    icon: Users,
    color: "#DC2626",
    bgClass: "bg-red-600",
    textClass: "text-red-700"
  }
};

// Custom marker icons by type
const createAlertIcon = (type) => {
  const color = ALERT_TYPES[type]?.color || "#EF4444";
  return L.divIcon({
    className: "custom-alert-marker",
    html: `<div style="
      width: 32px; 
      height: 32px; 
      background: ${color}; 
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="transform: rotate(45deg); color: white; font-size: 14px;">!</div>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

// Map controller for centering
const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 13);
    }
  }, [center, zoom, map]);
  return null;
};

const MissingAlertsPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([48.8566, 2.3522]); // Paris default
  const [showForm, setShowForm] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    last_seen_location: "",
    contact_phone: "",
    contact_name: ""
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [locating, setLocating] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const fileInputRef = useRef(null);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(loc);
          setMapCenter([loc.lat, loc.lng]);
        },
        (error) => {
          console.warn("Geolocation error:", error);
        }
      );
    }
  }, []);

  // Fetch nearby alerts
  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/alerts/missing`;
      
      // If we have location, fetch nearby alerts
      if (userLocation) {
        url = `${API}/alerts/missing/nearby?latitude=${userLocation.lat}&longitude=${userLocation.lng}&radius_km=50`;
      }
      
      const response = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      setAlerts(response.data.alerts || []);
      setStats(response.data.stats || null);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      // Try fetching all alerts if nearby fails
      try {
        const fallback = await axios.get(`${API}/alerts/missing`);
        setAlerts(fallback.data.alerts || []);
        setStats(fallback.data.stats || null);
      } catch (e) {
        toast.error("Erreur lors du chargement des alertes");
      }
    } finally {
      setLoading(false);
    }
  }, [token, userLocation]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Handle photo selection
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("La photo ne doit pas depasser 5 Mo");
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Get current address
  const getCurrentLocation = async () => {
    setLocating(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000
        });
      });

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;

      // Reverse geocode
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=fr`
      );
      const data = await response.json();
      
      setFormData(prev => ({
        ...prev,
        last_seen_location: data.display_name || `${lat}, ${lng}`,
        latitude: lat,
        longitude: lng
      }));
      
      toast.success("Position detectee");
    } catch (error) {
      toast.error("Impossible d'obtenir votre position");
    } finally {
      setLocating(false);
    }
  };

  // Submit alert
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedType) {
      toast.error("Veuillez selectionner un type d'alerte");
      return;
    }
    
    if (!formData.name || !formData.description || !formData.contact_phone) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setSubmitting(true);
    
    try {
      const submitData = new FormData();
      submitData.append("alert_type", selectedType);
      submitData.append("name", formData.name);
      submitData.append("description", formData.description);
      submitData.append("last_seen_location", formData.last_seen_location);
      submitData.append("contact_phone", formData.contact_phone);
      submitData.append("contact_name", formData.contact_name || user?.full_name || "");
      
      if (formData.latitude) {
        submitData.append("last_seen_latitude", formData.latitude);
        submitData.append("last_seen_longitude", formData.longitude);
      }
      
      if (photoFile) {
        submitData.append("photo", photoFile);
      }

      await axios.post(`${API}/alerts/missing`, submitData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Alerte creee ! La communaute est alertee.");
      setShowForm(false);
      resetForm();
      fetchAlerts();
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la creation");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedType(null);
    setFormData({
      name: "",
      description: "",
      last_seen_location: "",
      contact_phone: "",
      contact_name: ""
    });
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  // Mark as found
  const markAsFound = async (alertId) => {
    try {
      await axios.patch(
        `${API}/alerts/missing/${alertId}/status`,
        { status: "found" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Merci ! L'alerte a ete marquee comme resolue.");
      fetchAlerts();
      setSelectedAlert(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  // Share alert
  const shareAlert = async (alert) => {
    const shareText = `ALERTE DISPARITION: ${alert.name}\n${alert.description}\nContact: ${alert.contact_phone}\n\nAidez-nous a le/la retrouver !`;
    
    try {
      await axios.post(`${API}/alerts/missing/${alert.id}/share`);
      
      if (navigator.share) {
        await navigator.share({
          title: `Alerte Disparition - ${alert.name}`,
          text: shareText
        });
      } else {
        await navigator.clipboard.writeText(shareText);
        toast.success("Copie dans le presse-papiers !");
      }
    } catch (error) {
      console.error("Share error:", error);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black">
        <div className="max-w-6xl mx-auto px-4 py-6">
          
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              ALERTE DISPARITION
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">
              Signalez une disparition et mobilisez la communaute
            </p>
          </div>

          {/* Pulsating Emergency Button */}
          <div className="flex justify-center mb-8">
            <button
              data-testid="emergency-alert-button"
              onClick={() => setShowForm(true)}
              className="relative group"
            >
              {/* Pulsating rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-40 h-40 bg-red-500/20 rounded-full animate-ping" />
                <div className="absolute w-32 h-32 bg-red-500/30 rounded-full animate-pulse" />
              </div>
              
              {/* Main button */}
              <div className="relative w-36 h-36 sm:w-44 sm:h-44 bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-full flex flex-col items-center justify-center shadow-2xl shadow-red-500/50 transform transition-all duration-300 hover:scale-105 active:scale-95 border-4 border-red-400/50">
                <AlertTriangle className="w-12 h-12 sm:w-16 sm:h-16 text-white mb-2" />
                <span className="text-white font-bold text-sm sm:text-base text-center px-2">
                  SIGNALER
                </span>
                <span className="text-red-200 text-xs">
                  Disparition
                </span>
              </div>
            </button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              <Card className="bg-gray-800/60 border-gray-700 backdrop-blur">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-red-400">
                    {stats.total_searching || 0}
                  </div>
                  <div className="text-xs text-gray-400">En recherche</div>
                </CardContent>
              </Card>
              <Card className="bg-gray-800/60 border-gray-700 backdrop-blur">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-green-400">
                    {stats.total_found || 0}
                  </div>
                  <div className="text-xs text-gray-400">Retrouves</div>
                </CardContent>
              </Card>
              <Card className="bg-gray-800/60 border-gray-700 backdrop-blur">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-amber-400">
                    {stats.by_type?.animal || 0}
                  </div>
                  <div className="text-xs text-gray-400">Animaux</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Map Section */}
          <Card className="bg-gray-800/60 border-gray-700 backdrop-blur mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <MapPin className="w-5 h-5 text-red-400" />
                Carte des Alertes
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-64 sm:h-80 rounded-b-lg overflow-hidden">
                <MapContainer
                  center={mapCenter}
                  zoom={12}
                  style={{ height: "100%", width: "100%" }}
                  className="z-0"
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapController center={mapCenter} zoom={12} />
                  
                  {/* User location */}
                  {userLocation && (
                    <Circle
                      center={[userLocation.lat, userLocation.lng]}
                      radius={200}
                      pathOptions={{
                        color: "#EC4899",
                        fillColor: "#EC4899",
                        fillOpacity: 0.2
                      }}
                    />
                  )}
                  
                  {/* Alert markers */}
                  {alerts.map((alert) => (
                    alert.last_seen_latitude && alert.last_seen_longitude && (
                      <Marker
                        key={alert.id}
                        position={[alert.last_seen_latitude, alert.last_seen_longitude]}
                        icon={createAlertIcon(alert.alert_type)}
                        eventHandlers={{
                          click: () => setSelectedAlert(alert)
                        }}
                      >
                        <Popup>
                          <div className="text-center p-1">
                            <strong>{alert.name}</strong>
                            <p className="text-xs text-gray-600">
                              {ALERT_TYPES[alert.alert_type]?.label}
                            </p>
                          </div>
                        </Popup>
                      </Marker>
                    )
                  ))}
                </MapContainer>
              </div>
            </CardContent>
          </Card>

          {/* Alerts List */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Search className="w-5 h-5 text-red-400" />
              Alertes Actives ({alerts.filter(a => a.status === "searching").length})
            </h2>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-red-400" />
              </div>
            ) : alerts.filter(a => a.status === "searching").length === 0 ? (
              <Card className="bg-gray-800/60 border-gray-700">
                <CardContent className="p-6 text-center text-gray-400">
                  <Heart className="w-12 h-12 mx-auto mb-3 text-green-400" />
                  <p>Aucune alerte active dans votre zone.</p>
                  <p className="text-sm mt-1">C'est une bonne nouvelle !</p>
                </CardContent>
              </Card>
            ) : (
              alerts.filter(a => a.status === "searching").map((alert) => {
                const typeConfig = ALERT_TYPES[alert.alert_type] || ALERT_TYPES.animal;
                const Icon = typeConfig.icon;
                
                return (
                  <Card
                    key={alert.id}
                    data-testid={`alert-card-${alert.id}`}
                    className="bg-gray-800/60 border-gray-700 hover:border-red-500/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedAlert(alert)}
                  >
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Photo or Icon */}
                        <div className={`w-20 h-20 rounded-lg ${typeConfig.bgClass} flex items-center justify-center overflow-hidden flex-shrink-0`}>
                          {alert.photo_url ? (
                            <img
                              src={`${API.replace('/api', '')}${alert.photo_url}`}
                              alt={alert.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Icon className="w-10 h-10 text-white" />
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-white truncate">
                              {alert.name}
                            </h3>
                            <Badge 
                              variant="outline" 
                              className={`${typeConfig.textClass} border-current text-xs flex-shrink-0`}
                            >
                              {typeConfig.label}
                            </Badge>
                          </div>
                          
                          <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                            {alert.description}
                          </p>
                          
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {alert.last_seen_location?.substring(0, 30)}...
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(alert.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {alert.views_count || 0} vues
                            </span>
                            <span className="flex items-center gap-1">
                              <Share2 className="w-3 h-3" />
                              {alert.shares_count || 0} partages
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          {/* Creation Form Modal */}
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                  Nouvelle Alerte Disparition
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Type Selection */}
                <div>
                  <Label className="text-gray-300 mb-2 block">Type de disparition *</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(ALERT_TYPES).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          type="button"
                          data-testid={`alert-type-${key}`}
                          onClick={() => setSelectedType(key)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            selectedType === key
                              ? "border-red-500 bg-red-500/20"
                              : "border-gray-700 bg-gray-800 hover:border-gray-600"
                          }`}
                        >
                          <Icon className={`w-6 h-6 mx-auto mb-1 ${config.textClass}`} />
                          <span className="text-xs text-gray-300 block">
                            {config.label.split(" ")[0]}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Photo Upload */}
                <div>
                  <Label className="text-gray-300 mb-2 block">Photo (recommande)</Label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-700 rounded-lg p-4 cursor-pointer hover:border-gray-600 transition-colors"
                  >
                    {photoPreview ? (
                      <div className="relative">
                        <img
                          src={photoPreview}
                          alt="Preview"
                          className="max-h-40 mx-auto rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPhotoFile(null);
                            setPhotoPreview(null);
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500 rounded-full"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-center text-gray-500">
                        <Camera className="w-10 h-10 mx-auto mb-2" />
                        <p className="text-sm">Ajouter une photo</p>
                        <p className="text-xs">Max 5 Mo</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </div>

                {/* Name */}
                <div>
                  <Label className="text-gray-300">Nom / Prenom *</Label>
                  <Input
                    data-testid="alert-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder={selectedType === "animal" ? "Ex: Luna (chat tigre)" : "Ex: Jean Dupont"}
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <Label className="text-gray-300">Description *</Label>
                  <Textarea
                    data-testid="alert-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                    placeholder="Decrivez la personne/l'animal: age, taille, vetements, signes distinctifs..."
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                    rows={3}
                    required
                  />
                </div>

                {/* Last Seen Location */}
                <div>
                  <Label className="text-gray-300">Dernier lieu vu</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      data-testid="alert-location-input"
                      value={formData.last_seen_location}
                      onChange={(e) => setFormData(p => ({ ...p, last_seen_location: e.target.value }))}
                      placeholder="Adresse ou description du lieu"
                      className="bg-gray-800 border-gray-700 text-white flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={getCurrentLocation}
                      disabled={locating}
                      className="border-gray-700 text-gray-300 hover:bg-gray-800"
                    >
                      {locating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Navigation className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Contact */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-300">Telephone contact *</Label>
                    <Input
                      data-testid="alert-phone-input"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData(p => ({ ...p, contact_phone: e.target.value }))}
                      placeholder="06 12 34 56 78"
                      className="bg-gray-800 border-gray-700 text-white mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Nom contact</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData(p => ({ ...p, contact_name: e.target.value }))}
                      placeholder={user?.full_name || "Votre nom"}
                      className="bg-gray-800 border-gray-700 text-white mt-1"
                    />
                  </div>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  data-testid="submit-alert-button"
                  disabled={submitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Creation en cours...
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Creer l'Alerte
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  En creant cette alerte, vous acceptez nos conditions d'utilisation.
                  Les fausses alertes sont passibles de poursuites.
                </p>
              </form>
            </DialogContent>
          </Dialog>

          {/* Alert Detail Modal */}
          <Dialog open={!!selectedAlert} onOpenChange={() => setSelectedAlert(null)}>
            <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
              {selectedAlert && (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      {ALERT_TYPES[selectedAlert.alert_type]?.icon && 
                        React.createElement(ALERT_TYPES[selectedAlert.alert_type].icon, {
                          className: `w-5 h-5 ${ALERT_TYPES[selectedAlert.alert_type].textClass}`
                        })
                      }
                      {selectedAlert.name}
                    </DialogTitle>
                  </DialogHeader>

                  <div className="space-y-4">
                    {/* Photo */}
                    {selectedAlert.photo_url && (
                      <img
                        src={`${API.replace('/api', '')}${selectedAlert.photo_url}`}
                        alt={selectedAlert.name}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    )}

                    {/* Type Badge */}
                    <Badge className={`${ALERT_TYPES[selectedAlert.alert_type]?.bgClass} text-white`}>
                      {ALERT_TYPES[selectedAlert.alert_type]?.label}
                    </Badge>

                    {/* Description */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-1">Description</h4>
                      <p className="text-gray-200">{selectedAlert.description}</p>
                    </div>

                    {/* Location */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-1">Dernier lieu vu</h4>
                      <p className="text-gray-200 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-red-400" />
                        {selectedAlert.last_seen_location}
                      </p>
                    </div>

                    {/* Contact */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-1">Contact</h4>
                      <a
                        href={`tel:${selectedAlert.contact_phone}`}
                        className="flex items-center gap-2 text-pink-400 hover:text-pink-300"
                      >
                        <Phone className="w-4 h-4" />
                        {selectedAlert.contact_phone}
                        <span className="text-gray-500 text-sm">
                          ({selectedAlert.contact_name || "Anonyme"})
                        </span>
                      </a>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {selectedAlert.views_count || 0} vues
                      </span>
                      <span className="flex items-center gap-1">
                        <Share2 className="w-4 h-4" />
                        {selectedAlert.shares_count || 0} partages
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(selectedAlert.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={() => shareAlert(selectedAlert)}
                        variant="outline"
                        className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Partager
                      </Button>
                      
                      {(selectedAlert.reporter_id === user?.id || user?.is_admin) && (
                        <Button
                          onClick={() => markAsFound(selectedAlert.id)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Retrouve !
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

        </div>
      </div>
    </Layout>
  );
};

export default MissingAlertsPage;
